import IconButton from '@mui/material/IconButton'
// Low severity icon
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
// Medium severity icon
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined'
// High severity icon
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'

// Auto severity icon
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesome'

import Tooltip from '@mui/material/Tooltip'
import ButtonGroup from '@mui/material/ButtonGroup'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import { useState } from 'react'

const autoDescription = 'The AI will automatically determine the severity of the issue'
const lowDescription = 'Minor issue that has minimal impact on compliance'
const mediumDescription = 'Moderate issue that has potential impact on compliance'
const criticalDescription = 'Critical issue that has a high potential impact on compliance'

type SeverityToggleProps = {
  severity: number
  onChange: (severity: number) => void
  disabled?: boolean
}

const SeverityToggle = ({ severity, onChange, disabled }: SeverityToggleProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setAnchorEl(null)
  }

  const handleChange = (newSeverity: number, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    onChange(newSeverity)
    handleClose(e)
  }

  const open = Boolean(anchorEl)

  const getSeverityIcon = () => {
    switch (severity) {
      case 0:
        return <AutoAwesomeOutlinedIcon />
      case 1:
        return <InfoOutlinedIcon />
      case 2:
        return <ReportOutlinedIcon />
      case 3:
        return <NewReleasesOutlinedIcon />
    }
  }

  const getSeverityDescription = () => {
    switch (severity) {
      case 0:
        return autoDescription
      case 1:
        return lowDescription
      case 2:
        return mediumDescription
      case 3:
        return criticalDescription
    }
  }

  const getSeverityColor = () => {
    switch (severity) {
      case 0:
        return 'inherit'
      case 1:
        return 'warning'
      case 2:
        return 'warning'
      case 3:
        return 'error'
      default:
        return 'inherit'
    }
  }

  return (
    <>
      <Tooltip title={getSeverityDescription()}>
        <IconButton
          onClick={handleClick}
          size="small"
          disabled={disabled}
          color={getSeverityColor()}
          data-qa-id="measure-severity-button"
        >
          {getSeverityIcon()}
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(45deg)',
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              borderTop: '1px solid rgba(0,0,0,0.12)',
              borderLeft: '1px solid rgba(0,0,0,0.12)',
              zIndex: 0,
            },
            overflow: 'visible',
          },
        }}
      >
        <ButtonGroup variant="outlined" size="small">
          <Tooltip title={autoDescription}>
            <Button
              onClick={(e) => handleChange(0, e)}
              variant={severity === 0 ? 'contained' : 'outlined'}
              startIcon={<AutoAwesomeOutlinedIcon />}
              data-qa-id="measure-severity-option-auto"
            >
              Auto
            </Button>
          </Tooltip>
          <Tooltip title={lowDescription}>
            <Button
              onClick={(e) => handleChange(1, e)}
              variant={severity === 1 ? 'contained' : 'outlined'}
              startIcon={<InfoOutlinedIcon />}
              data-qa-id="measure-severity-option-low"
            >
              Low
            </Button>
          </Tooltip>
          <Tooltip title={mediumDescription}>
            <Button
              onClick={(e) => handleChange(2, e)}
              variant={severity === 2 ? 'contained' : 'outlined'}
              startIcon={<ReportOutlinedIcon />}
              data-qa-id="measure-severity-option-medium"
            >
              Medium
            </Button>
          </Tooltip>
          <Tooltip title={criticalDescription}>
            <Button
              onClick={(e) => handleChange(3, e)}
              variant={severity === 3 ? 'contained' : 'outlined'}
              startIcon={<NewReleasesOutlinedIcon />}
              data-qa-id="measure-severity-option-critical"
            >
              Critical
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Popover>
    </>
  )
}

export default SeverityToggle
